import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { createDraft } from '@services/partnerService';
import { usePartnerAuth } from '../PartnerAuthContext';
import { AddressAutocomplete, type AddressValue } from '../AddressAutocomplete';
import { isValidLocal, toStoredPhone } from '../phone';
import { Button, Card, ErrorNote, Field, Muted, PhoneField, Title } from '../ui';

const CreateCommerce = () => {
  const { token } = usePartnerAuth();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [nombre, setNombre] = useState('');
  const [address, setAddress] = useState<AddressValue>({ direccion: '', lat: null, lng: null });
  const [ownerNombre, setOwnerNombre] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerWhatsapp, setOwnerWhatsapp] = useState(''); // 10 digitos nacionales

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError(null);

    // WhatsApp es el destino del OTP del claim: bloquea el alta si no es un numero MX valido.
    if (!isValidLocal(ownerWhatsapp)) {
      setPhoneError('Ingresa un WhatsApp valido de 10 digitos.');
      return;
    }
    setPhoneError(null);

    setCreating(true);
    try {
      const res = await createDraft(token, {
        negocio: {
          nombre: nombre.trim(),
          direccion: address.direccion.trim() || undefined,
          lat: address.lat ?? undefined,
          lng: address.lng ?? undefined,
        },
        owner: { nombre: ownerNombre.trim(), email: ownerEmail.trim(), whatsapp: toStoredPhone(ownerWhatsapp) },
        monthly_orders: 'menos_de_100',
        product_interest: ['menu'],
      });
      navigate(`/partner/comercios/${res.id_comercio}`);
    } catch {
      setError('No se pudo crear el comercio. Revisa los datos.');
      setCreating(false);
    }
  };

  return (
    <>
      <Title sub="Crea el comercio y su suscripción FREEMIUM. Luego podrás cargar el menú y generar el enlace de activación para el dueño.">
        Nuevo comercio
      </Title>
      <Card style={{ maxWidth: 520 }}>
        <form onSubmit={onCreate}>
          <Muted style={{ marginTop: 0, marginBottom: 12, fontWeight: 600, color: '#253746' }}>Negocio</Muted>
          <Field label="Nombre del negocio" value={nombre} onChange={setNombre} required />
          <AddressAutocomplete value={address} onChange={setAddress} />
          <Muted style={{ marginTop: 8, marginBottom: 12, fontWeight: 600, color: '#253746' }}>Dueño</Muted>
          <Field label="Nombre del dueño" value={ownerNombre} onChange={setOwnerNombre} required />
          <Field label="Email del dueño" type="email" value={ownerEmail} onChange={setOwnerEmail} required />
          <PhoneField
            label="WhatsApp del dueño"
            value={ownerWhatsapp}
            onChange={(v) => {
              setOwnerWhatsapp(v);
              if (phoneError) setPhoneError(null);
            }}
            error={phoneError}
            required
          />
          {error && <ErrorNote>{error}</ErrorNote>}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <Button type="submit" disabled={creating}>
              {creating ? 'Creando...' : 'Crear comercio'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/partner/comercios')}
              disabled={creating}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
};

export default CreateCommerce;
